import { useQuery } from "@tanstack/react-query";
import {
  PlayOnePagerNotice,
  PlayOnePagerRecommendations,
} from "@softmax/product-ux/play";
import { useDiscardUnavailableData } from "@softmax/web-features/data";
import type { PublicPlayShell } from "../../lib/types";
import { Yours } from "./your-programs";
import { ProgramRow, type ProgramViewer } from "./program-row";
import { recommendedOptions } from "../one-pager.queries";
import type { OnePagerData } from "../types";
export function OnePagerPrograms({
  data,
  pending,
  shell,
  corpus,
  name,
  path,
  viewerId,
}: {
  readonly data: Pick<OnePagerData, "yours"> | undefined;
  readonly pending: boolean;
  readonly shell: PublicPlayShell;
  readonly corpus: string;
  readonly name: string;
  readonly path: string;
  readonly viewerId: string | null;
}) {
  const viewer: ProgramViewer = {
    coworld: name,
    leagueId: shell.league.id,
    playPath: path,
    divisionId: shell.divisionId,
    corpus,
  };
  return (
    <>
      {data ? (
        <Yours yours={data.yours} viewer={viewer} />
      ) : (
        <PlayOnePagerNotice
          state={
            pending
              ? { kind: "loading", message: "Loading your champion…" }
              : { kind: "empty", message: "Programs unavailable." }
          }
        />
      )}
      <Recommendations viewer={viewer} viewerId={viewerId} />
    </>
  );
}
function Recommendations({
  viewer,
  viewerId,
}: {
  readonly viewer: ProgramViewer;
  readonly viewerId: string | null;
}) {
  const options = recommendedOptions({
    href: viewer.playPath,
    viewerId,
  });
  const read = useQuery(options);
  const unavailable = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: read.error,
  });
  const result = unavailable ? undefined : read.data;
  const programs = result?.programs;
  const currentViewer = {
    ...viewer,
    divisionId: result ? result.divisionId : viewer.divisionId,
  };
  const caption =
    programs?.[0]?.source === "starter"
      ? "starters from the makers"
      : "the top of the leaderboard";
  return (
    <PlayOnePagerRecommendations
      caption={programs ? caption : null}
      notice={
        <>
          {read.isError ? (
            <PlayOnePagerNotice
              state={{
                kind: "error",
                message: programs
                  ? "Recommendations could not refresh. Showing the last update."
                  : "Recommendations unavailable.",
                pending: read.isFetching,
                onRetry: () => void read.refetch(),
              }}
            />
          ) : null}
          {read.isPending ? (
            <PlayOnePagerNotice
              state={{ kind: "loading", message: "Loading…" }}
            />
          ) : null}
          {programs?.length === 0 ? (
            <PlayOnePagerNotice
              state={{ kind: "empty", message: "Nothing to recommend yet." }}
            />
          ) : null}
        </>
      }
      programs={
        programs?.length
          ? programs.map((program) => (
              <ProgramRow
                key={program.label}
                label={program.label}
                meta={program.by ?? ""}
                id={program.id}
                by={program.by}
                starter={program.source === "starter"}
                viewer={currentViewer}
              />
            ))
          : null
      }
    />
  );
}
